import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseKey);

export interface SaveSystemPromptParams {
  name: string;
  type: 'system' | 'user';
  content: string;
  feature: string;
  sub_feature: string | null;
  default_prompt?: boolean;
  is_active?: boolean;
}

export const savePrompt = async (params: SaveSystemPromptParams) => {
  const {
    name,
    type,
    content,
    feature,
    sub_feature,
    default_prompt = true,
    is_active = true,
  } = params;

  const { data, error } = await supabase
    .from('system_prompts')
    .insert({
      name,
      type,
      content,
      feature,
      sub_feature,
      default_prompt,
      is_active,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export interface SystemPrompt {
  id: string;
  name: string;
  type: 'system' | 'user';
  content: string;
  feature: string;
  sub_feature: string;
  default_prompt: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const getPrompts = async (feature?: string, sub_feature?: string) => {
  let query = supabase.from('system_prompts').select('*').order('updated_at', { ascending: false });

  if (feature) {
    query = query.eq('feature', feature);
  }

  if (sub_feature) {
    query = query.eq('sub_feature', sub_feature);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as SystemPrompt[];
};

export const updatePrompt = async (id: string, params: Partial<SaveSystemPromptParams>) => {
  const { data, error } = await supabase
    .from('system_prompts')
    .update(params)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateDefaultPrompt = async (
  id: string,
  params: { name?: string; content?: string; is_active?: boolean }
) => {
  // For default prompts, only allow updating name and content
  const updateData: { name?: string; content?: string; is_active?: boolean } = {};

  if (params.name !== undefined) {
    updateData.name = params.name;
  }

  if (params.content !== undefined) {
    updateData.content = params.content;
  }

  if (params.is_active !== undefined) {
    updateData.is_active = params.is_active;
  }

  const { data, error } = await supabase
    .from('system_prompts')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deletePrompt = async (id: string) => {
  const { error } = await supabase.from('system_prompts').delete().eq('id', id);

  if (error) throw error;
};

export { supabase };
